import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

export type Tema = "light" | "dark";

interface TemaProviderProps {
  children: ReactNode;
  temaPorDefecto?: Tema;
  storageKey?: string;
}

interface TemaState {
  tema: Tema;
  setTema: (tema: Tema) => void;
}

const TemaContext = createContext<TemaState | undefined>(undefined);

function esTema(valor: string | null): valor is Tema {
  return valor === "light" || valor === "dark";
}

/**
 * Solo para el arranque: si todavia no eligieron tema, empezamos por el que
 * usa la maquina. Despues manda lo guardado y no volvemos a mirar el sistema.
 */
function preferenciaDelSistema(): Tema {
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function aplicarTema(tema: Tema): void {
  const root = document.documentElement;
  root.classList.remove("light", "dark");
  root.classList.add(tema);
  root.style.colorScheme = tema;
}

export function ThemeProvider({
  children,
  temaPorDefecto,
  storageKey = "tora-tema",
}: TemaProviderProps): JSX.Element {
  const [tema, setTemaState] = useState<Tema>(() => {
    if (typeof window === "undefined") return temaPorDefecto ?? "light";
    try {
      const guardado = localStorage.getItem(storageKey);
      if (esTema(guardado)) return guardado;
    } catch {
      // localStorage no disponible (modo privado). Seguimos con el default.
    }
    return temaPorDefecto ?? preferenciaDelSistema();
  });

  useEffect(() => {
    aplicarTema(tema);
  }, [tema]);

  const value = useMemo<TemaState>(
    () => ({
      tema,
      setTema: (next: Tema) => {
        try {
          localStorage.setItem(storageKey, next);
        } catch {
          // localStorage no disponible (modo privado). El tema vive solo en memoria.
        }
        setTemaState(next);
      },
    }),
    [tema, storageKey],
  );

  return <TemaContext.Provider value={value}>{children}</TemaContext.Provider>;
}

export function useTema(): TemaState {
  const ctx = useContext(TemaContext);
  if (!ctx) throw new Error("useTema tiene que usarse dentro de <ThemeProvider>.");
  return ctx;
}
